import React, { useCallback, useRef, useState, useEffect } from 'react'

import isequal from 'lodash.isequal'

import { Project, ProjectsApiListProjectsRequest } from '@pollination-solutions/pollination-sdk'
import { SelectProjectProps } from './SelectProject.types'

import { ComboBox } from '../atoms'
import { useDebounce } from '../hooks/useDebounce'


const longDebounce = useDebounce(500)
const shortDebounce = useDebounce(200)

export const SelectProject: React.FC<SelectProjectProps> = ({
  authUser,
  client,
  projectOwner,
  value,
  onChange,
}) => {

  const comboBoxRef = useRef<any>()

  const valueRef = useRef<Project>()

  const [selProject, setSelProject] = useState<Project>()
  const [loading, setLoading] = useState(false)
  const [projects, setProjects] = useState<Project[]>([])
  const [pageNumber, setPageNumber] = useState<number>(1)
  
  const totalProjectsRef = useRef({
    loaded: 0,
    total: undefined
  })

  const [total, setTotal] = useState(0)
  const projectApiRef = useRef<ProjectsApiListProjectsRequest>({
    owner: [projectOwner] ?? (authUser && [authUser.username]),
    page: pageNumber,
    perPage: 10,
  })

  // resetting query
  useEffect(() => {
    // reset total loaded
    totalProjectsRef.current = {
      loaded: 0,
      total: undefined
    }
    
    // deselect item
    if(comboBoxRef.current) {
      comboBoxRef.current.selectItem(null)
    }

    setProjects([])
    setPageNumber(1)
  }, [projectOwner])

  useEffect(() => {
    // select item
    if(comboBoxRef.current) {
      comboBoxRef.current.selectItem(value)
    }
  }, [value])

  longDebounce(
    () => {
      projectApiRef.current = {
        owner: [projectOwner] ?? (authUser && [authUser.username]),
        page: pageNumber,
        perPage: 10,
      }
  
      const queryConfig = projectApiRef.current
      if (!authUser || !client || !client.projects) return
  
      if ( !queryConfig.owner ) return

      const { total, loaded } = totalProjectsRef.current
      if ( total && loaded >= total ) return
      
      const fetchData = async () => {
        const { data } = await client.projects.listProjects(queryConfig)
        setProjects(state => state 
          ? [...state, ...data.resources] 
          : [...data.resources])
        setTotal(data.total_count)
        totalProjectsRef.current.loaded += data.resources.length
        totalProjectsRef.current.total = data.total_count
      }

      // call the function
      setLoading(true)

      fetchData()
      .catch(console.error)
      .finally(() => setLoading(false))
    },
    [authUser, client, projectOwner, pageNumber]
  )

  shortDebounce(
    () => {
      // nonmemoized value from react-hook-form or other parent causes infinite rerendering
      if (isequal(valueRef.current, selProject)) {
        return
      }

      if (selProject && selProject.name) {
        comboBoxRef.current.setInputValue( selProject.name )
      }
      
      comboBoxRef.current.selectItem( selProject )
      valueRef.current = selProject
      
      // don't call onChange for undefined values
      onChange && onChange(selProject)
    },
    [selProject, onChange]
  )

  const onScrollReachEnd = useCallback(() => {
    // already loaded all runs
    const { total, loaded } = totalProjectsRef.current
    if ( total && loaded >= total ) return

    setPageNumber(projectApiRef.current.page + 1)
  }, [])

  return (
    <ComboBox
      ref={comboBoxRef}
      items={projects ?? []}
      setSelected={setSelProject}
      inputProps={{
        placeholder: 'Select a project...',
      }}
      loading={loading}
      disabled={ !authUser || !projectOwner }
      footer={(
        <div
          key="footer"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            color: 'var(--grey4)',
          }}
        >
          {`Showing ${projects.length ?? 0} of ${total} projects`}
        </div>
      )}
      onScrollReachEnd={onScrollReachEnd}
    />
  )
}

export default SelectProject
